import React, { useState, useEffect } from "react";
import ResponsiveDrawer from "../../component/sidebar/sidebar.component";
import { FontFamily } from "../../component/theme/fontFamily";
import { fontWeight } from "../../component/theme/fontWeight";
import { fontSize } from "../../component/theme/fontSize";
import {
  Box,
  Grid,
  IconButton,
  Typography,
  Avatar,
  Divider,
  Stack,
  Button,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Chip
} from "@mui/material";
import { ThemeColor } from "../../component/theme/color";
import dayjs from "dayjs";
import CustomButton from "../../component/button/button";
import OutlineCustomButton from "../../component/button/outlineButton";
import Settinglist from "../../Icon/Settinglist";
import { useDispatch, useSelector } from "react-redux";

import Message from "../../Icon/Message";
import { useNavigate } from "react-router-dom";
import { GetMailReplyPayload, GetSentMailAction, SetMailReplyPayload } from "../../Redux/action/EmailAction";
import { CustomPagination } from "../../component/datatable/CustomPagination";
import { resolveInlineImages } from "../../helper/helper";
import { fetchEmailBodyAndAttachments } from "../../Redux/service/MailService";
import { Reply, ArrowBack } from "@mui/icons-material";

const SentEmaillistPage = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [attachments, setAttachments] = useState([]);
  const [mailBody, setMailBody] = useState(""); 
  const [bodyloading, setbodyLoading] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [mobileView, setMobileView] = useState("list");

  const handleReply = () => {
    const payload = {
      selectedEmail,
      mailBody,
      attachments,
    };
    dispatch(SetMailReplyPayload(payload));
    navigate("/CAE/Sendmail");
  };

  const [SentEmaillist, setSentEmaillist] = useState([]);
  const [table_load, setTable_load] = useState(false);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 50,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  const fetchBodyAndAttachments = async (mail) => {
    if (!mail || !mail.id) return;
    try {
      setbodyLoading(true);
      const response = await fetchEmailBodyAndAttachments(window.sessionStorage.getItem("ms_auth"), mail.id);
      setMailBody(response.data.body || "");
      setAttachments(response.data.attachments || []);
      setbodyLoading(false);
    } catch (error) {
      console.error("Error fetching mail body and attachments:", error);
      setbodyLoading(false);
    }
  };

  const [limit, setLimit] = useState(50);

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPagination((prev) => ({ ...prev, currentPage: 1, limit: newLimit }));
  };

  const SentEmaillistState = useSelector((state) => state.email?.sentemail);

  useEffect(() => {
    if (selectedEmail) {
      fetchBodyAndAttachments(selectedEmail);
    }
  }, [selectedEmail]);

  useEffect(() => {
    if (!SentEmaillistState) return;

    const paginationData =
      SentEmaillistState?.pagination ?? SentEmaillistState?.data?.pagination ?? SentEmaillistState?.data?.data?.pagination;

    if (paginationData) {
      const currentPage = paginationData.currentPage || paginationData.page || 1;
      const hasNextPage = paginationData.hasNextPage !== undefined 
        ? paginationData.hasNextPage 
        : (currentPage * (paginationData.limit || 50) < (paginationData.totalCount || 0));
      const estimatedTotalPages = hasNextPage ? currentPage + 1 : currentPage;

      setPagination((prev) => ({
        ...prev,
        currentPage: currentPage,
        totalPages: estimatedTotalPages,
        totalCount: paginationData.totalCount || 0,
        limit: paginationData.limit || prev.limit,
        hasNextPage: hasNextPage,
        hasPrevPage: currentPage > 1,
      }));
      if (paginationData.limit) setLimit(paginationData.limit);
    }

    const emailData = SentEmaillistState?.data?.data ?? SentEmaillistState?.data ?? SentEmaillistState;
    if (Array.isArray(emailData)) {
      const updatedData = emailData.map(({ _id, messageId, ...rest }, index) => ({
        id: _id || messageId || index,
        ...rest,
      }));
      setSentEmaillist(updatedData);
      if (updatedData.length > 0 && !selectedEmail) {
        setSelectedEmail(updatedData[0]);
      }
    } else if (SentEmaillistState !== undefined) {
      setSentEmaillist([]);
    }

    setTable_load(false);
  }, [SentEmaillistState, selectedEmail]);

  useEffect(() => {
    setTable_load(true);
    dispatch(GetSentMailAction({ page: pagination.currentPage, limit: pagination.limit }));
  }, [dispatch, pagination.currentPage, pagination.limit]);

  return (
    <Box sx={{ p: 1, height: "calc(100vh - 120px)", display: "flex", flexDirection: "column" }}>
      {/* Top Header Controls Block */}
      {mobileView === "list" && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item lg={8} md={7} xs={12}>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography
                fontFamily={FontFamily.Poppins}
                fontWeight={fontWeight.W600}
                fontSize={fontSize.h2.small}
                color="#111827"
              >
                Sent Items
              </Typography>
              <Box
                sx={{
                  backgroundColor: "rgba(106, 27, 154, 0.1)",
                  color: ThemeColor.PURPLE,
                  px: 2, py: 0.5,
                  borderRadius: "20px",
                  fontSize: "13px",
                  fontWeight: "600"
                }}
              >
                {SentEmaillist.length} Sent Messages
              </Box>
            </Box>
          </Grid>
        </Grid>
      )}

      {/* Main Mail Split-Screen Window Layout */}
      <Box sx={{ flexGrow: 1, display: "flex", gap: 2, overflow: "hidden", minHeight: 400 }}>
        
        {/* Left Side: Sent Email List Snippet Track Column */}
        <Paper variant="outlined" sx={{ width: "100%", display: mobileView === "list" ? "flex" : "none", flexDirection: "column", bgcolor: "background.paper", borderRadius: "16px", overflow: "hidden", border: "1px solid #e2e8f0", boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.02)" }}>
          {table_load ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", py: 8, flexGrow: 1 }}>
              <LinearProgress sx={{ width: "80%" }} />
              <Typography sx={{ mt: 2, color: "#6B7280" }}>Loading sent items...</Typography>
            </Box>
          ) : SentEmaillist.length === 0 ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flexGrow: 1 }}>
              <Typography color="textSecondary">No sent messages found.</Typography>
            </Box>
          ) : (
            <List sx={{ flexGrow: 1, overflowY: "auto", p: 0 }}>
              {SentEmaillist.map((item) => {
                const isSelected = selectedEmail?.id === item.id;
                return (
                  <React.Fragment key={item.id}>
                    <ListItemButton
                      onClick={() => {
                        setSelectedEmail(item);
                        setMobileView("details");
                      }}
                      selected={isSelected}
                      sx={{
                        p: 2,
                        alignItems: "flex-start",
                        flexDirection: "column",
                        borderLeft: isSelected ? `4px solid ${ThemeColor.PURPLE}` : "4px solid transparent",
                        bgcolor: isSelected ? "rgba(106, 27, 154, 0.06)" : "inherit",
                        "&:hover": { bgcolor: "rgba(0, 0, 0, 0.02)" },
                        transition: "all 0.2s ease"
                      }}
                    >
                      <Box sx={{ display: "flex", width: "100%", justifyContent: "space-between", mb: 0.5, alignItems: "center" }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.primary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "70%" }}>
                          {item.to && item.to.length > 0 ? item.to.join(", ") : "Recipient"}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {dayjs(item.receivedDateTime).format("MMM DD, hh:mm A")}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center", width: "100%", gap: 0.5, mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 400, color: isSelected ? "primary.main" : "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", width: "100%" }}>
                          {item.subject || "(No Subject)"}
                        </Typography>
                      </Box>
                    </ListItemButton>
                    <Divider />
                  </React.Fragment>
                );
              })}
            </List>
          )}

          {/* Pagination nested bottom control */}
          <Box sx={{ p: 1, borderTop: "1px solid #e0e0e0", bgcolor: "#f8f9fa" }}>
            <CustomPagination
              pagination={pagination}
              onPageChange={handlePageChange}
              limit={limit}
              onLimitChange={handleLimitChange}
            />
          </Box>
        </Paper>

        {/* Right Side: Document Reader Screen Panel */}
        <Paper variant="outlined" sx={{ flexGrow: 1, width: "100%", display: mobileView === "details" ? "flex" : "none", flexDirection: "column", bgcolor: "background.paper", borderRadius: "16px", overflow: "hidden", border: "1px solid #e2e8f0", boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.02)" }}>
          {selectedEmail ? (
            <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
              {/* Active Item Metadata Header */}
              <Box sx={{ py: 1.5, px: 2, borderBottom: "1px solid #e2e8f0", bgcolor: "#f8fafc" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                  <IconButton
                    onClick={() => setMobileView("list")}
                    sx={{ p: 0.5, color: "#64748b" }}
                  >
                    <ArrowBack sx={{ fontSize: 20 }} />
                  </IconButton>
                  <Typography variant="subtitle1" sx={{ fontFamily: FontFamily.Poppins, fontWeight: 600, color: "#0f172a", flexGrow: 1, fontSize: "14px", lineHeight: 1.2 }}>
                    {selectedEmail.subject || "(No Subject)"}
                  </Typography>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1, flexWrap: "nowrap" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                    <Avatar sx={{ bgcolor: "rgba(106, 27, 154, 0.08)", color: ThemeColor.PURPLE, fontWeight: 600, width: 32, height: 32, fontSize: "12px" }}>
                      {selectedEmail.to && selectedEmail.to.length > 0 ? selectedEmail.to[0].charAt(0).toUpperCase() : "R"}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontFamily: FontFamily.Poppins, fontWeight: 600, color: "#1e293b", fontSize: "12.5px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        To: {selectedEmail.to && selectedEmail.to.join(", ")}
                      </Typography>
                      <Typography variant="caption" sx={{ fontFamily: FontFamily.Nunito, color: "#64748b", fontSize: "10.5px" }}>
                        Sent: {dayjs(selectedEmail.receivedDateTime).format("MMM DD, YYYY [at] hh:mm A")}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                    <Button
                      variant="outlined"
                      startIcon={<Reply />}
                      onClick={handleReply}
                      sx={{
                        color: ThemeColor.PURPLE,
                        borderColor: ThemeColor.PURPLE,
                        "&:hover": {
                          borderColor: ThemeColor.PURPLE,
                          backgroundColor: "rgba(106, 27, 154, 0.04)"
                        },
                        textTransform: "none",
                        borderRadius: "6px",
                        fontSize: "11px",
                        fontWeight: 600,
                        py: 0.5,
                        px: 1.5,
                        height: "28px"
                      }}
                    >
                      Reply
                    </Button>
                  </Box>
                </Box>
              </Box>

              {bodyloading ? (
                <Box sx={{ flex: 1, alignItems: "center", justifyContent: "center", display: "flex", py: 4 }}>
                  <LinearProgress sx={{ width: "80%" }} />
                </Box>
              ) : (
                <Box sx={{ flexGrow: 1, p: 3, overflowY: "auto", bgcolor: "#ffffff" }}>
                  {mailBody ? (
                    <div
                      style={{ fontFamily: "inherit", fontSize: "0.95rem", lineHeight: 1.6 }}
                      dangerouslySetInnerHTML={{
                        __html: resolveInlineImages(mailBody, attachments)
                      }}
                    />
                  ) : (
                    <Typography variant="body2" color="textSecondary" sx={{ fontStyle: "italic" }}>
                      This message has no body contents.
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", flexDirection: "column", gap: 1 }}>
              <Typography color="textSecondary" variant="body2">
                Select an item to preview details.
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

function SentEmaillist() {
  return (
    <div>
      <ResponsiveDrawer maincontent={<SentEmaillistPage />} />
    </div>
  );
}

export default SentEmaillist;